// ═══════════════════════════════════════════════════════════════════
// tomeMotion.js — THE SPELLBOOK · H24 · Terror TCG
// 72 Tomes · 9 Curios · 8 Prophecies · 4 Affinities.
// The largest score file in the game, and the one that decides whether
// a duel READS.
// ═══════════════════════════════════════════════════════════════════
//
// ── WHY THIS FILE MATTERS MORE THAN THE OTHER THREE ────────────────
// Beings are visible. A player watches a Spectral Tiger take a rift and
// knows what happened without being told. Tomes are the opposite: 72
// cards that mostly move NUMBERS, and until now every single one of
// them entered on the same Casting and then produced no motion at all
// when it actually resolved. The most consequential moment in most
// turns — a Nihil ward eating a U'hlti, an Omnia Tome ruining a Soul —
// was, visually, nothing happening.
//
// ── THE EIGHT SCHOOLS ARE EIGHT VERBS, AND THAT IS THE WHOLE SYSTEM ─
// Terror's Tome Ilks are not eight elements, they are eight design
// philosophies, and each one maps to exactly one primitive. Learn the
// verb once and you can read any of the 72 on sight:
//
//   Construction     LATTICE   it is built, and then it stays built
//   Treasure         HALO      it belongs to one being now
//   Nihil            VEIL      the effect is refused; the fare stays paid
//   Omnia            SHATTER   the card is destroyed, not merely stopped
//   Ancient Sorcery  VORTEX    somewhere in the Grimoire, the right page
//   Ritual           GRAVE     the Necropolis is not a one-way door
//   Shadow Gambling  COIN      J'ynxi is watching, and she is delighted
//   Contract         CHAIN     a legally binding offer to your enemy
//
// The pair that matters most is NIHIL vs OMNIA — the spine of every
// counter-play decision in the game. A veil CLOSES. A shatter OPENS.
// They are opposite motions in opposite palettes and you can tell which
// one resolved from across the room without reading a word. Getting
// that pair right is most of the value in this file.
//
// ── LEVEL IS SCALE, NEVER A NEW IDEA ───────────────────────────────
// Most schools ship three cards of the same effect at Lvl 1/2/3 (Not
// Here → Not Now → Not Ever!; Seal → Monoliths → Mausoleum). Those are
// deliberately the SAME score with more of it — bigger radius, more
// ticks, a longer hold. Inventing three unrelated images for one
// escalating idea would actively hide the escalation, and the player
// learning "wider means higher level" is worth more than novelty.
//
// `casting()` already scales its rune's tick count by Level, so a good
// deal of that happens for free.
// ═══════════════════════════════════════════════════════════════════

import {
  P, score, casting, seal, toll, wound,
  SURGE, slash, motes, pillar, spiral, bolt,
  stream, glyph, grave, rune, veil, shatter,
  chain, coin, crack, vortex, embers, lattice,
  halo, star, rift, echoes, sever, constellation,
  mirror, plume, thorn, scan
} from "./motionKit";

const S = { to: "self" };

// ── A LOCAL BUILDER, BECAUSE 72 OF ANYTHING NEEDS ONE ──────────────
// Every Tome score is `casting(school, level)` plus what makes it
// itself. Writing that first line 72 times by hand is 72 chances to
// pass the wrong school and get a Nihil ward in Omnia crimson — which
// would be a real bug, silent, and exactly the kind this file's whole
// colour law exists to prevent.
const tome = (name, ilk, level, cues, ms) =>
  score(name, [...casting(ilk, level), ...cues], ms);

export const TOMES = {

// ═══════════════════════════════════════════════════════════════════
// CONSTRUCTION · "it is built, and then it stays built"
// ═══════════════════════════════════════════════════════════════════
// Nine Tomes of pure economy — Max Khao, Anima, and three walls. The
// lattice ASSEMBLES and then holds at full brightness before releasing,
// which is the only motion in the vocabulary that ends more solid than
// it began. Everything else in this file dissipates.

  // +1 Max Khao at end of turn. The smallest permanent thing in Terror.
  tome_khao_garden: tome("Khao Garden", "Construction", 1, [
    lattice(300, P.gold, { ...S, ms: 560, r: 0.6, n: 12, size: 0.04 }),
    embers(520, P.emberGold, { ...S, ms: 480, n: 14, r: 0.45, size: 0.06, size2: 1.0 }),
  ], 900),

  // +2 Max Khao. The same garden, grown.
  tome_khao_arcadia: tome("Khao Arcadia", "Construction", 2, [
    lattice(300, P.gold, { ...S, ms: 620, r: 0.85, n: 12, size: 0.05 }),
    embers(540, P.emberGold, { ...S, ms: 540, n: 20, r: 0.65, size: 0.065, size2: 1.3 }),
    ...toll(P.gold, 600, 1, "self"),
  ], 1020),

  // +2 Max Khao AND intangible the turn it is built. Construction's only
  // card that is also a ward, so it is the only one that wears a veil.
  tome_khao_paradise: tome("Khao Paradise", "Construction", 3, [
    lattice(300, P.gold, { ...S, ms: 680, r: 1.05, n: 12, size: 0.055 }),
    veil(600, P.goldPale, { ...S, ms: 620, r: 1.3, size: 0.72 }),
    embers(660, P.emberGold, { ...S, ms: 560, n: 26, r: 0.8, size: 0.07, size2: 1.6 }),
    ...toll(P.gold, 700, 2, "self"),
  ], 1300),

  // +1 Anima. A TREE — the one Construction card that grows rather than
  // assembles, so it is the one that gets the spiral instead of the box.
  // The Exception Law, spent on the card that is literally named for a
  // living thing.
  tome_bhodi_tree: tome("Bhodi Tree", "Construction", 1, [
    spiral(280, P.gold, { ...S, ms: 620, tone: "up", spin: 2.2, r: 0.5, size: 2.0 }),
    embers(520, P.returnGreen, { ...S, ms: 520, n: 18, r: 0.5, size: 0.065, size2: 1.4 }),
  ], 940),

  // +2 Anima. A house: built, then warm.
  tome_anima_house: tome("Anima House", "Construction", 2, [
    lattice(290, P.gold, { ...S, ms: 640, r: 0.8, n: 12, size: 0.05 }),
    embers(560, P.animaGreen, { ...S, ms: 560, n: 22, r: 0.6, size: 0.07, size2: 1.5 }),
    ...toll(P.goldPale, 620, 1, "self"),
  ], 1060),

  // +5 Anima and intangible the turn built. The largest Anima swing any
  // Tome offers, and the name is the direction: the roof comes off.
  tome_stars_and_skies: tome("The Stars and Skies", "Construction", 3, [
    lattice(280, P.gold, { ...S, ms: 600, r: 1.0, n: 12, size: 0.05 }),
    pillar(560, P.starWhite, { ...S, ms: 660, r: 6.0, size: 0.44, spin: 0.3 }),
    star(760, P.starCore, { ...S, ms: 420, r: 1.8, size: 0.05, tone: 20 }),
    star(860, P.starWhite, { ...S, ms: 420, r: 1.5, size: 0.045, tone: -30 }),
    embers(820, P.animaGreen, { ...S, ms: 600, n: 28, r: 0.9, size: 0.07, size2: 1.9 }),
  ], 1480),

  // ── THE THREE WALLS ─────────────────────────────────────────────
  // "Your adversary must ruin this card before attacking another." A
  // taunt is an ADVERTISEMENT, not a defence — the same reading that
  // produced H-01's Natural Lyt — so all three throw a chain across the
  // seam. What escalates is how much wall is behind it.
  tome_khao_duhmdumn_beb: tome("Khao D'uhmdumn Beb", "Construction", 1, [
    lattice(290, P.gold, { ...S, ms: 560, r: 0.65, n: 12, size: 0.055 }),
    chain(500, P.brass, { from: "self", to: "target", ms: 480, r: 0.7, size: 0.09 }),
  ], 900),

  tome_khao_duhmdumn_champion: tome("Khao D'uhmdumn Champion", "Construction", 2, [
    lattice(290, P.gold, { ...S, ms: 620, r: 0.85, n: 12, size: 0.06 }),
    chain(520, P.brass, { from: "self", to: "target", ms: 520, r: 0.78, size: 0.1 }),
    ...toll(P.gold, 580, 1, "self"),
  ], 1060),

  // ...and the first attack on this one is nullified — so the Lord wall
  // is the only one of the three that also closes a veil.
  tome_khao_duhmdumn_lord: tome("Khao D'uhmdumn Lord", "Construction", 3, [
    lattice(290, P.gold, { ...S, ms: 680, r: 1.05, n: 12, size: 0.065 }),
    veil(560, P.white, { ...S, ms: 600, r: 1.25, size: 0.7 }),
    chain(620, P.brass, { from: "self", to: "target", ms: 540, r: 0.85, size: 0.11 }),
    ...toll(P.gold, 700, 2, "self"),
  ], 1280),

// ═══════════════════════════════════════════════════════════════════
// TREASURE · "it belongs to one being now"
// ═══════════════════════════════════════════════════════════════════
// Nine Tomes that sit on a Tome rift but BOND to a being — the only
// school whose cards have an owner. So every one of them ends on the
// wielder rather than on the field, and the halo is what says "this is
// attached to that."

  // "Feed up to 4 Anima per turn; after 8, ruin 1 being and its Soul."
  // A treasure that eats. The embers here are Anima going IN, not
  // radiating out — the only inverted use of the primitive in the file
  // — and what it buys is a sever.
  tome_necklace_death: tome("Necklace of Congealed Death", "Treasure", 1, [
    halo(280, P.omnia, { ...S, ms: 620, r: 0.85, size: 0.7, spin: 1.5 }),
    embers(360, P.bloodDeep, { ...S, ms: 560, n: 22, r: 0.55, size: 0.07, size2: 1.2 }),
    sever(700, P.crimson, { ms: 560, r: 1.7, size: 0.042, size2: 0.3, tone: -20 }),
    ...toll(P.omnia, 760, 2),
  ], 1340),

  // "Feed up to 3 Khao per turn; after 6, gain 6 Anima." The same
  // feeding shape as its sibling above, paid in the other currency and
  // returning warmth instead of a cut. Deliberately the same score with
  // the ending swapped — they are twin cards and should read as twins.
  tome_necklace_courage: tome("Necklace of Joyous Courage", "Treasure", 2, [
    halo(280, P.goldPale, { ...S, ms: 620, r: 0.9, size: 0.72, spin: 1.5 }),
    embers(380, P.khaoBlue, { ...S, ms: 560, n: 22, r: 0.55, size: 0.07, size2: 1.2 }),
    embers(720, P.animaGreen, { ...S, ms: 620, n: 30, r: 0.8, size: 0.075, size2: 1.9 }),
    ...toll(P.goldPale, 760, 2, "self"),
  ], 1400),

  // "Intangible for the first 3 turns. +1 Anima whenever a being is
  // ruined." A ward that profits from death.
  tome_necklace_akula: tome("Necklace of Akula", "Treasure", 3, [
    halo(280, P.goldPale, { ...S, ms: 700, r: 0.95, size: 0.75, spin: 1.2 }),
    veil(400, P.white, { ...S, ms: 640, r: 1.3, size: 0.72 }),
    embers(700, P.animaGreen, { ...S, ms: 540, n: 20, r: 0.6, size: 0.065, size2: 1.4 }),
  ], 1280),

  // "When the wielder Lyt Attacks, dim the Lyf of your adversary's
  // cards by 1." The only Treasure named for a weapon element, and the
  // only one that gets BOLT — violence as lightning, per fx3D's note.
  tome_lightning_blade: tome("Lightning Blade", "Treasure", 1, [
    halo(260, P.goldPale, { ...S, ms: 520, r: 0.8, size: 0.68, spin: 2.2 }),
    bolt(440, P.starWhite, { from: "self", to: "target", ms: 260, r: 0.36 }),
    bolt(560, P.cyanHot, { from: "self", to: "target", ms: 220, r: 0.24 }),
    ...wound(P.starWhite, 640, 2),
  ], 1160),

  // "The wielder gains an additional Lyt Attack." Extra attacks are
  // ECHOES everywhere in this game; the sword is what casts them.
  tome_sword_of_shadows: tome("Sword of Shadows", "Treasure", 2, [
    halo(270, P.shadow, { ...S, ms: 560, r: 0.85, size: 0.7, spin: 1.8 }),
    echoes(430, P.amethyst, { from: "self", to: "target", ms: 540, n: 4, size: 0.5, tone: 0.42 }),
    slash(820, P.violetIce, { ms: 460, n: 1, r: 1.2, tone: 0 }),
  ], 1300),

  // "Ruin 1 being and its Soul." No feeding, no condition, 10 Khao. The
  // purest execution card in the Treasure school and it gets the purest
  // sever in the file — long, slow, and the halo above it goes out as
  // the blade lands.
  tome_weeping_dagger: tome("Weeping Dagger", "Treasure", 3, [
    halo(260, P.goldPale, { ...S, ms: 480, r: 0.85, size: 0.72, spin: 2.6 }),
    embers(420, P.violetPale, { ms: 560, n: 16, r: 0.5, size: 0.06, size2: 1.1 }),
    sever(620, P.white, { ms: 680, r: 2.1, size: 0.05, size2: 0.4, tone: -22 }),
    shatter(880, P.goldPale, { ms: 620, n: 12, r: 1.3 }),
    ...toll(P.omnia, 900, 3),
  ], 1620),

  // "Its wielder gains +2 Lyt." Gauntlets: the lattice closes around a
  // pair of fists rather than around a plot of ground.
  tome_gauntlets_azka: tome("Gauntlets of A'zka", "Treasure", 1, [
    halo(260, P.goldPale, { ...S, ms: 520, r: 0.8, size: 0.66, spin: 1.6 }),
    lattice(400, P.gold, { ...S, ms: 520, r: 0.5, n: 12, size: 0.05 }),
    star(760, P.judgeGold, { ...S, ms: 340, r: 1.0, size: 0.045 }),
  ], 1120),

  // "Make 1 of your beings Intangible." Intangibility is always a veil.
  // The rising motes are the boots leaving the ground.
  tome_boots_wing_walking: tome("Boots of Wing Walking", "Treasure", 2, [
    halo(260, P.goldPale, { ...S, ms: 540, r: 0.85, size: 0.72, spin: 1.4 }),
    embers(400, P.starWhite, { ...S, ms: 560, n: 20, r: 0.5, size: 0.065, size2: 1.7 }),
    veil(560, P.white, { ...S, ms: 620, r: 1.3, size: 0.76 }),
  ], 1220),

  // "Transfer this card's Lyf to any 1 card, for 3 turns." A transfer
  // is a STREAM with two ends named — the only Treasure that leaves the
  // wielder at all.
  tome_chief_calidum: tome("Chief Calidum's Cuirass", "Treasure", 3, [
    halo(260, P.goldPale, { ...S, ms: 520, r: 0.9, size: 0.72, spin: 1.5 }),
    stream(420, P.goldPale, { from: "self", to: "target", ms: 620, n: 40, r: 0.2, size: 0.06 }),
    halo(880, P.gold, { ms: 620, r: 0.9, size: 0.72, spin: 1.5 }),
  ], 1520),

// ═══════════════════════════════════════════════════════════════════
// NIHIL · "the effect is nullified; the fare stays paid"
// ═══════════════════════════════════════════════════════════════════
// Nine refusals, and the most important nine cards in this file to get
// visually right. THE VEIL SNAPS UP — it never eases, never blooms,
// never bursts. Something arrives to say no, holds flat and bright, and
// thins out. White and cyan, always. If a Nihil card ever reads as an
// explosion, this school has failed.

  // "Nullify the effect... if it's an Omnia Tome, ruin it." The counter
  // that counters counters. Veil first, and the star INSIDE it is the
  // Omnia Tome being taken apart by the refusal itself.
  tome_rays_of_hope: tome("Rays of Hope", "Nihil", 1, [
    veil(280, P.white, { ...S, ms: 600, r: 1.3, size: 0.74 }),
    star(420, P.starWhite, { ...S, ms: 400, r: 1.5, size: 0.05 }),
    ...toll(P.white, 460, 1, "self"),
  ], 940),

  // Same, one level up, and it eats Curios too. The burst is in the
  // NAME, so this is the one Nihil card allowed a scatter — outward
  // motes, immediately after the dome, and no further.
  tome_cleansing_burst: tome("Cleansing Burst of Nihil", "Nihil", 2, [
    veil(280, P.white, { ...S, ms: 620, r: 1.5, size: 0.78 }),
    motes(460, P.starWhite, { ...S, ms: 520, n: 30, r: 1.9, size: 0.07 }),
    ...toll(P.white, 500, 2, "self"),
  ], 1120),

  // "Nullify all Omnia Tomes for 2 turns." A standing ward, not an
  // event — so the rune stays inscribed under the dome and the halo
  // holds. The widest veil in the school.
  tome_divine_justice: tome("Divine Justice", "Nihil", 3, [
    veil(280, P.white, { ...S, ms: 720, r: 2.0, size: 0.8 }),
    rune(400, P.starWhite, { ...S, ms: 720, r: 1.8, n: 16, spin: 0.3 }),
    halo(640, P.cyanHot, { ...S, ms: 640, r: 1.1, size: 0.85, spin: 1.0 }),
    ...toll(P.white, 700, 2, "self"),
  ], 1420),

  // "Your beings cannot be attacked using Tomes this turn." Covers the
  // WHOLE side, not one card — so the veil is enormous and low, more
  // weather than shield, and the light rains down into it.
  tome_shower_of_lyt: tome("Shower of Lyt", "Nihil", 1, [
    veil(280, P.starWhite, { ...S, ms: 680, r: 2.4, size: 0.5 }),
    stream(420, P.white, { from: "selfsky", to: "self", ms: 620, n: 46, r: 0.7, size: 0.06 }),
  ], 1100),

  // "Nullify 1 attack... if the attacker is an Omnia Tome, ruin it."
  // The plainest ward in the game: dome, crown, done.
  tome_radiant_aura: tome("Radiant Aura", "Nihil", 2, [
    veil(280, P.white, { ...S, ms: 620, r: 1.4, size: 0.76 }),
    halo(420, P.starWhite, { ...S, ms: 620, r: 1.0, size: 0.8, spin: 1.3 }),
  ], 1060),

  // "Your cards cannot be attacked using Tomes for 2 turns." A PHOENIX
  // — the one Nihil card with heat in it, and the only place in this
  // school embers are allowed.
  tome_phoenix_aura: tome("Phoenix Aura", "Nihil", 3, [
    veil(280, P.white, { ...S, ms: 720, r: 2.2, size: 0.78 }),
    embers(400, P.rust, { ...S, ms: 680, n: 34, r: 1.0, size: 0.08, size2: 2.2 }),
    halo(640, P.emberGold, { ...S, ms: 680, r: 1.15, size: 0.88, spin: 1.1 }),
    ...toll(P.white, 700, 2, "self"),
  ], 1420),

  // "If your Anima would be dimmed this turn, ABSORB the damage and add
  // it to your Anima." The single strangest card in the school — it
  // does not refuse the damage, it EATS it — so it is the only Nihil
  // Tome that gets a vortex, running inward, under the dome.
  tome_wall_of_light: tome("Wall of Light", "Nihil", 1, [
    veil(280, P.white, { ...S, ms: 660, r: 1.5, size: 0.8 }),
    vortex(400, P.cyanHot, { ...S, ms: 620, r: 1.2, n: 44, spin: 5.0, size: 0.8 }),
    embers(760, P.animaGreen, { ...S, ms: 560, n: 24, r: 0.7, size: 0.07, size2: 1.6 }),
  ], 1360),

  // "Nullify 1 attack being made to 1 of your beings." Quick, small,
  // cheap — the fastest score in the school, and it should be: this is
  // a reflex, not a ritual.
  tome_lyt_footed: tome("Lyt Footed", "Nihil", 2, [
    veil(270, P.white, { ...S, ms: 480, r: 1.15, size: 0.7 }),
    motes(400, P.starWhite, { ...S, ms: 400, n: 16, r: 1.0, size: 0.06, tone: "in" }),
  ], 880),

  // "Nullify 1 attack... if the attacking card was a Tome, ruin it."
  // Sword AND shield, and it is the only Nihil card that answers back:
  // dome, then a sever leaving it. The one moment this school is
  // permitted to cut.
  tome_sword_shield_nihil: tome("Sword and Shield of Nihil", "Nihil", 3, [
    veil(280, P.white, { ...S, ms: 660, r: 1.6, size: 0.8 }),
    sever(560, P.starWhite, { ms: 600, r: 1.9, size: 0.045, size2: 0.32, tone: -16 }),
    ...toll(P.cyanHot, 620, 2),
  ], 1340),

// ═══════════════════════════════════════════════════════════════════
// OMNIA · "the card is destroyed, not merely stopped"
// ═══════════════════════════════════════════════════════════════════
// The other half of the spine. Where Nihil closes, Omnia OPENS —
// crimson, outward, and always ending in debris. The grammar is fixed
// across every ruin effect in every file in this game:
//
//     CRACK (the ground fails) → SHATTER (the thing comes apart)
//
// A player who has seen it twice never has to be told again.

  // "Dim your own Anima by 1; ruin 1 Tome or Curios." The cost is paid
  // in embers before anything breaks — same grammar as Eternal
  // Nightmare, and for the same reason: a price is a beat, not a note.
  tome_nothing_left: tome("Nothing Left", "Omnia", 1, [
    embers(280, P.bloodDeep, { ...S, ms: 440, n: 14, r: 0.45, size: 0.06, size2: 1.0 }),
    shatter(480, P.omnia, { ms: 620, n: 12, r: 1.3 }),
    ...toll(P.crimson, 520, 1),
  ], 1120),

  // "Ruin 1 Tome or Curios." No cost. The rune closes on the target
  // first — a sentence being read before it is carried out.
  tome_regnant: tome("Regnant", "Omnia", 2, [
    rune(280, P.omnia, { ms: 560, r: 1.2, n: 10, spin: 0.6, tone: "close" }),
    shatter(560, P.omnia, { ms: 640, n: 14, r: 1.5 }),
    ...toll(P.crimson, 600, 2),
  ], 1240),

  // "If your adversary attempts to activate a Tome, or one is in use,
  // ruin it instead." A standing trap. The rune is laid on THEIR half
  // and waits; the shatter is what springs.
  tome_all_or_nothing: tome("All or Nothing", "Omnia", 3, [
    rune(280, P.omnia, { ms: 680, r: 1.7, n: 14, spin: 0.7, tone: "close" }),
    crack(560, P.bloodDeep, { ms: 620, n: 7, r: 2.4 }),
    shatter(720, P.omnia, { ms: 680, n: 16, r: 1.8 }),
    ...toll(P.crimson, 760, 3),
  ], 1520),

  // "Dim your own Anima by 1; ruin 1 Impera." An Impera is a BRAND —
  // pressed in and never removed — so removing one is not a shatter, it
  // is a sever. The one Omnia card that cuts before it breaks.
  tome_radiant_armor: tome("Radiant Armor", "Omnia", 1, [
    embers(280, P.bloodDeep, { ...S, ms: 420, n: 12, r: 0.4, size: 0.06, size2: 0.9 }),
    sever(460, P.crimson, { ms: 540, r: 1.5, size: 0.04, size2: 0.26, tone: -14 }),
    shatter(680, P.omnia, { ms: 540, n: 9, r: 1.0 }),
  ], 1240),

  // "Ruin 1 Impera or U'hlti." A JET, by name — so the corruption
  // travels as a stream before it lands, and it is the only Omnia card
  // that crosses the seam visibly rather than resolving over there.
  tome_omnis_corruption: tome("Omnis' Corruption Jet", "Omnia", 2, [
    stream(280, P.omnia, { from: "self", to: "target", ms: 560, n: 44, r: 0.18, size: 0.06 }),
    sever(700, P.crimson, { ms: 520, r: 1.6, size: 0.042, size2: 0.28, tone: 12 }),
    shatter(860, P.omnia, { ms: 600, n: 13, r: 1.4 }),
    ...toll(P.crimson, 900, 2),
  ], 1520),

  // "Dim your own Anima by 1; ruin 1 Impera AND 1 U'hlti." Two things
  // die, so there are two severs, crossed. The only score in the game
  // with an X in it.
  tome_superque: tome("Superque", "Omnia", 3, [
    embers(280, P.bloodDeep, { ...S, ms: 420, n: 14, r: 0.45, size: 0.06, size2: 1.0 }),
    sever(480, P.crimson, { ms: 560, r: 1.9, size: 0.045, size2: 0.3, tone: -26 }),
    sever(620, P.omnia, { ms: 560, r: 1.9, size: 0.045, size2: 0.3, tone: 26 }),
    shatter(880, P.omnia, { ms: 640, n: 16, r: 1.7 }),
    ...toll(P.crimson, 920, 3),
  ], 1620),

  // "Ruin 1 Non-Being." The plainest destruction in the school, and it
  // gets the canonical pair with nothing else on top.
  tome_wall_of_life: tome("Wall of Life", "Omnia", 1, [
    crack(280, P.bloodDeep, { ms: 520, n: 6, r: 2.0 }),
    thorn(400, P.omnia, { ms: 520, n: 7, r: 1.0, size: 0.9 }),
    shatter(520, P.omnia, { ms: 620, n: 12, r: 1.4 }),
    ...toll(P.crimson, 520, 1),
  ], 1160),

  // "Dim your own Anima by 1; ruin 1 CARD." Any card. No qualifier.
  tome_lyt_hearted: tome("Lyt Hearted", "Omnia", 2, [
    embers(280, P.bloodDeep, { ...S, ms: 420, n: 14, r: 0.45, size: 0.06, size2: 1.0 }),
    crack(460, P.bloodDeep, { ms: 580, n: 7, r: 2.2 }),
    shatter(660, P.omnia, { ms: 640, n: 14, r: 1.6 }),
    ...toll(P.crimson, 700, 2),
  ], 1380),

  // "If your adversary attempts to activate a card EFFECT, or one is in
  // use, ruin it instead." The widest denial in the game — it does not
  // name a card type at all. The loudest Omnia score, and the only one
  // that opens a crack under the seam rather than under a card.
  tome_sword_shield_omnis: tome("Sword and Shield of O'mnis", "Omnia", 3, [
    rune(280, P.omnia, { to: "seam", ms: 660, r: 1.8, n: 16, spin: 0.7, tone: "close" }),
    crack(560, P.bloodDeep, { to: "seam", ms: 700, n: 10, r: 3.2 }),
    thorn(680, P.omnia, { to: "seam", ms: 620, n: 10, r: 1.5, size: 1.1 }),
    sever(820, P.crimson, { ms: 620, r: 2.1, size: 0.05, size2: 0.36, tone: -18 }),
    shatter(940, P.omnia, { ms: 680, n: 18, r: 1.9 }),
    ...toll(P.crimson, 980, 3),
  ], 1740),

// ═══════════════════════════════════════════════════════════════════
// ANCIENT SORCERY · "somewhere in the Grimoire, the right page"
// ═══════════════════════════════════════════════════════════════════
// Nine searches. The VORTEX reaches in; the RIFT is what comes back
// out. `tone: "out"` reverses the funnel, which is how the three
// send-then-fetch cards get to say both halves with one primitive.

  // Not Here / Not Now / Not Ever! — the same reach at three depths.
  // Nothing changes but scale, deliberately: this is one idea, levelled.
  tome_not_here: tome("Not Here", "Ancient Sorcery", 1, [
    // THE LOOKING. The vortex below is the moment the hand
    // closes on the page; nothing said the searching that came
    // first. A constant-rate sweep — an eased one looks like it
    // found the card early and is showing off.
    scan(300, P.sorcery, { ...S, ms: 460, r: 1.00, size: 0.045, tone: 0 }),
    vortex(280, P.sorcery, { ...S, ms: 560, r: 1.0, n: 40, spin: 5.0, size: 0.7 }),
    rift(620, P.amethyst, { ...S, ms: 440, r: 1.1, size: 0.03, size2: 0.24 }),
  ], 1060),

  tome_not_now: tome("Not Now", "Ancient Sorcery", 2, [
    // THE LOOKING. The vortex below is the moment the hand
    // closes on the page; nothing said the searching that came
    // first. A constant-rate sweep — an eased one looks like it
    // found the card early and is showing off.
    scan(300, P.sorcery, { ...S, ms: 500, r: 1.15, size: 0.045, tone: 0 }),
    vortex(280, P.sorcery, { ...S, ms: 620, r: 1.3, n: 50, spin: 5.8, size: 0.85 }),
    rift(680, P.amethyst, { ...S, ms: 480, r: 1.4, size: 0.034, size2: 0.28 }),
    ...toll(P.sorcery, 720, 1, "self"),
  ], 1220),

  tome_not_ever: tome("Not Ever!", "Ancient Sorcery", 3, [
    // THE LOOKING. The vortex below is the moment the hand
    // closes on the page; nothing said the searching that came
    // first. A constant-rate sweep — an eased one looks like it
    // found the card early and is showing off.
    scan(300, P.sorcery, { ...S, ms: 540, r: 1.30, size: 0.045, tone: 0 }),
    vortex(280, P.sorcery, { ...S, ms: 700, r: 1.6, n: 60, spin: 6.6, size: 1.0 }),
    rift(740, P.violetIce, { ...S, ms: 540, r: 1.8, size: 0.04, size2: 0.34 }),
    star(860, P.amethyst, { ...S, ms: 380, r: 1.4, size: 0.045 }),
    ...toll(P.sorcery, 800, 2, "self"),
  ], 1460),

  // ── THE THREE BLOOD PRICES ──────────────────────────────────────
  // "Send 1 card to Necropolis, THEN add 1 from your Grimoire." Two
  // motions in a fixed order, and the vortex says both: `out` is the
  // sending, plain is the fetching. Read together they are one hand
  // putting something down and picking something else up.
  tome_blood_in_blood_out: tome("Blood in Blood Out", "Ancient Sorcery", 1, [
    vortex(280, P.omnia, { ...S, ms: 520, r: 1.0, n: 34, spin: 4.4, size: 0.7, tone: "out" }),
    vortex(620, P.sorcery, { ...S, ms: 560, r: 1.1, n: 40, spin: 5.2, size: 0.75 }),
    rift(940, P.amethyst, { ...S, ms: 420, r: 1.1, size: 0.03, size2: 0.24 }),
  ], 1400),

  tome_blood_money: tome("Blood Money", "Ancient Sorcery", 2, [
    vortex(280, P.bloodDeep, { ...S, ms: 540, r: 1.1, n: 36, spin: 4.6, size: 0.75, tone: "out" }),
    vortex(640, P.sorcery, { ...S, ms: 580, r: 1.25, n: 46, spin: 5.6, size: 0.85 }),
    rift(980, P.amethyst, { ...S, ms: 460, r: 1.3, size: 0.033, size2: 0.27 }),
  ], 1480),

  // A RIVER — the one card in the school where what is sent flows
  // rather than falls, so it gets the stream the other two do not.
  tome_river_of_blood: tome("River of Blood", "Ancient Sorcery", 3, [
    stream(280, P.omnia, { from: "self", to: "back", ms: 600, n: 50, r: 0.24, size: 0.06 }),
    vortex(680, P.sorcery, { ...S, ms: 640, r: 1.5, n: 56, spin: 6.2, size: 0.95 }),
    rift(1060, P.violetIce, { ...S, ms: 500, r: 1.7, size: 0.038, size2: 0.32 }),
    ...toll(P.sorcery, 1100, 2, "self"),
  ], 1660),

  // "Search your Luxmoire for a lvl 2 Impera." The Luxmoire is the
  // side deck — a different place entirely — so this one converges from
  // outside the board rather than funnelling down into it.
  tome_shadow_blanket: tome("Shadow Blanket", "Ancient Sorcery", 1, [
    // THE LOOKING. The vortex below is the moment the hand
    // closes on the page; nothing said the searching that came
    // first. A constant-rate sweep — an eased one looks like it
    // found the card early and is showing off.
    scan(300, P.amethyst, { ...S, ms: 440, r: 1.00, size: 0.045, tone: 90 }),
    motes(280, P.shadow, { ...S, ms: 560, n: 34, r: 2.0, size: 0.07, tone: "in" }),
    vortex(560, P.sorcery, { ...S, ms: 520, r: 0.95, n: 36, spin: 4.8, size: 0.7 }),
  ], 1120),

  // "Add 1 U'hlti from your Grimoire to your hand." An INVERTED KNIFE —
  // so the vortex runs and the blade comes out of it pointing the wrong
  // way. The only search in the school that ends on a cut.
  tome_inverted_knife: tome("Inverted Knife", "Ancient Sorcery", 2, [
    // THE LOOKING. The vortex below is the moment the hand
    // closes on the page; nothing said the searching that came
    // first. A constant-rate sweep — an eased one looks like it
    // found the card early and is showing off.
    scan(300, P.sorcery, { ...S, ms: 440, r: 1.05, size: 0.045, tone: 0 }),
    vortex(280, P.sorcery, { ...S, ms: 600, r: 1.2, n: 44, spin: 5.4, size: 0.8, tone: "out" }),
    sever(660, P.violetIce, { ...S, ms: 540, r: 1.4, size: 0.04, size2: 0.26, tone: 24 }),
    ...toll(P.amethyst, 700, 1, "self"),
  ], 1260),

  // "Add 1 Impera from your Luxmoire AND 1 U'hlti from your Grimoire."
  // Two zones, two returns — the only Sorcery card that opens two
  // rifts, and they are deliberately offset so you can count them.
  tome_light_casting_shadow: tome("Light Casting Shadow", "Ancient Sorcery", 3, [
    // THE LOOKING. The vortex below is the moment the hand
    // closes on the page; nothing said the searching that came
    // first. A constant-rate sweep — an eased one looks like it
    // found the card early and is showing off.
    scan(300, P.sorcery, { ...S, ms: 520, r: 1.25, size: 0.045, tone: 0 }),
    vortex(280, P.sorcery, { ...S, ms: 660, r: 1.5, n: 56, spin: 6.0, size: 0.95 }),
    rift(700, P.starWhite, { ...S, ms: 480, r: 1.5, size: 0.036, size2: 0.3, tone: -10 }),
    rift(820, P.shadow, { ...S, ms: 480, r: 1.5, size: 0.036, size2: 0.3, tone: 10 }),
    ...toll(P.sorcery, 860, 2, "self"),
  ], 1500),

// ═══════════════════════════════════════════════════════════════════
// RITUAL · "the Necropolis is not a one-way door"
// ═══════════════════════════════════════════════════════════════════
// Nine recursions. GRAVE is the verb — marks rising out of the felt —
// and the school splits three ways by what it brings back: Omnis pulls
// Tomes, Nihil pulls Imperas, and the Soulcerers pull BEINGS. Only the
// last three are true resurrections and only they get the pillar, which
// is the Rite of Return's own signature borrowed on purpose.

  tome_seal_of_omnis: tome("Seal of Omnis", "Ritual", 1, [
    grave(280, P.ritual, { ...S, ms: 700, n: 5, r: 0.7, size: 22, tone: "⛧" }),
    vortex(600, P.phantom, { ...S, ms: 520, r: 1.0, n: 34, spin: 4.6, size: 0.7 }),
  ], 1180),

  tome_monoliths_of_omnis: tome("Monoliths of Omnis", "Ritual", 2, [
    grave(280, P.ritual, { ...S, ms: 740, n: 6, r: 0.85, size: 25, tone: "⛧" }),
    lattice(520, P.violetDeep, { ...S, ms: 620, r: 0.9, n: 12, size: 0.06 }),
    vortex(760, P.phantom, { ...S, ms: 540, r: 1.2, n: 40, spin: 5.0, size: 0.8 }),
  ], 1360),

  tome_mausoleum_of_omnis: tome("Mausoleum of Omnis", "Ritual", 3, [
    grave(280, P.ritual, { ...S, ms: 800, n: 8, r: 1.0, size: 28, tone: "⛧" }),
    lattice(540, P.violetDeep, { ...S, ms: 680, r: 1.15, n: 12, size: 0.065 }),
    vortex(820, P.phantom, { ...S, ms: 600, r: 1.45, n: 50, spin: 5.6, size: 0.9 }),
    ...toll(P.ritual, 860, 2, "self"),
  ], 1560),

  // The Nihil arm — same rite, white marks, pulling brands back.
  tome_seal_of_nihil: tome("Seal of Nihil", "Ritual", 1, [
    grave(280, P.white, { ...S, ms: 700, n: 5, r: 0.7, size: 22, tone: "⊘" }),
    vortex(600, P.starWhite, { ...S, ms: 520, r: 1.0, n: 34, spin: 4.6, size: 0.7 }),
  ], 1180),

  tome_monoliths_of_nihil: tome("Monoliths of Nihil", "Ritual", 2, [
    grave(280, P.white, { ...S, ms: 740, n: 6, r: 0.85, size: 25, tone: "⊘" }),
    lattice(520, P.starWhite, { ...S, ms: 620, r: 0.9, n: 12, size: 0.06 }),
    vortex(760, P.white, { ...S, ms: 540, r: 1.2, n: 40, spin: 5.0, size: 0.8 }),
  ], 1360),

  tome_mausoleum_of_nihil: tome("Mausoleum of Nihil", "Ritual", 3, [
    grave(280, P.white, { ...S, ms: 800, n: 8, r: 1.0, size: 28, tone: "⊘" }),
    lattice(540, P.starWhite, { ...S, ms: 680, r: 1.15, n: 12, size: 0.065 }),
    vortex(820, P.cyanHot, { ...S, ms: 600, r: 1.45, n: 50, spin: 5.6, size: 0.9 }),
    ...toll(P.white, 860, 2, "self"),
  ], 1560),

  // ── THE SOULCERERS ──────────────────────────────────────────────
  // "Resurrect 1 being from Necropolis." These three are the only
  // Tomes in the game that put a BEING back on the field, and they
  // deliberately quote the Rite of Return — skulls, then a column, then
  // green. R44/R77: the ground does not care which method paid.
  tome_soulcerers_seal: tome("Soulcerer's Seal", "Ritual", 1, [
    grave(280, P.griefViolet, { ...S, ms: 760, n: 6, r: 0.75, size: 24, tone: "☠" }),
    pillar(520, P.returnGreen, { ...S, ms: 560, r: 3.8, size: 0.4, spin: 0.35 }),
    embers(760, P.returnCore, { ...S, ms: 520, n: 22, r: 0.6, size: 0.07, size2: 1.5 }),
  ], 1340),

  tome_soulcerers_monoliths: tome("Soulcerer's Monoliths", "Ritual", 2, [
    grave(280, P.griefViolet, { ...S, ms: 800, n: 7, r: 0.9, size: 26, tone: "☠" }),
    pillar(540, P.returnGreen, { ...S, ms: 620, r: 4.6, size: 0.44, spin: 0.35 }),
    embers(820, P.returnCore, { ...S, ms: 560, n: 28, r: 0.75, size: 0.075, size2: 1.8 }),
    ...toll(P.griefViolet, 860, 2, "self"),
  ], 1520),

  // "Resurrect 1 being up to lvl 3, OR 2 lvl 1 beings." The largest
  // resurrection available to any card, and the only Ritual Tome that
  // opens two rifts — one per being, for the two-Beb reading.
  tome_soulcerers_mausoleum: tome("Soulcerer's Mausoleum", "Ritual", 3, [
    grave(280, P.griefViolet, { ...S, ms: 860, n: 9, r: 1.05, size: 29, tone: "☠" }),
    plume(340, P.griefViolet, { ...S, ms: 640, n: 26, r: 1.1, size: 0.1, size2: 0.9, tone: 0.5 }),
    pillar(600, P.returnGreen, { ...S, ms: 700, r: 5.4, size: 0.5, spin: 0.35 }),
    rift(880, P.returnCore, { ...S, ms: 460, r: 1.3, size: 0.034, size2: 0.26, tone: -9 }),
    rift(980, P.returnCore, { ...S, ms: 460, r: 1.3, size: 0.034, size2: 0.26, tone: 9 }),
    embers(1000, P.animaGreen, { ...S, ms: 600, n: 32, r: 0.9, size: 0.08, size2: 2.0 }),
  ], 1720),

// ═══════════════════════════════════════════════════════════════════
// SHADOW GAMBLING · "J'ynxi is watching, and she is delighted"
// ═══════════════════════════════════════════════════════════════════
// Nine gambles, every one of them Lvl 3, and the COIN carries all of
// them. The primitive spends 70% of its life in the air and lands
// STILL and bright — the stillness at the end is the result being read,
// and it is the whole reason these nine feel different from everything
// else in the game despite sharing one image.
//
// The variable that separates them is HOW MANY and HOW HIGH. One coin
// is a flip. Two is dice. A higher throw is a bigger bet. Nothing else
// needed changing.

  // "Roll 1 dice. If you roll the chosen number..." 0 Khao — the
  // cheapest gamble, so the lowest throw.
  tome_temptation_manifested: tome("Temptation Manifested", "Shadow Gambling", 3, [
    coin(280, P.jynxi, { ...S, ms: 660, r: 1.5, size: 0.3, spin: 8 }),
    glyph(820, P.jynxi, { ...S, ms: 460, tone: "⚄", size: 38, r: 0.55 }),
  ], 1300),

  // "Roll 2 dice. If they are BOTH the chosen number..." The longest
  // odds among the Tomes, and the card the Casino is named for. Two
  // coins, landing apart.
  tome_snake_eyes: tome("Snake Eyes", "Shadow Gambling", 3, [
    coin(280, P.jynxi, { ...S, ms: 720, r: 1.9, size: 0.3, spin: 9 }),
    coin(350, P.swarm, { ...S, ms: 760, r: 2.2, size: 0.3, spin: 11 }),
    glyph(980, P.jynxi, { ...S, ms: 520, tone: "⚀", size: 42, r: 0.7 }),
    ...toll(P.jynxi, 1020, 2, "self"),
  ], 1600),

  tome_nevermore: tome("Nevermore", "Shadow Gambling", 3, [
    coin(280, P.shadow, { ...S, ms: 660, r: 1.6, size: 0.3, spin: 8 }),
    motes(820, P.violetDeep, { ...S, ms: 480, n: 22, r: 1.3, size: 0.07 }),
  ], 1320),

  // "Pick 1 card and put it up for BET." The card is staked before it
  // is thrown for — so a rift opens first (the card leaving your hand
  // to sit on the table) and only then does the coin go up.
  tome_underdogs_howl: tome("Underdog's Howl", "Shadow Gambling", 3, [
    rift(280, P.jynxi, { ...S, ms: 440, r: 1.1, size: 0.03, size2: 0.24 }),
    coin(480, P.jynxi, { ...S, ms: 720, r: 2.0, size: 0.31, spin: 9 }),
    ...toll(P.jynxi, 1120, 2, "self"),
  ], 1500),

  // "Your adversary gets 1 try to guess its Lvl." The gamble is in
  // THEIR hands, so the coin is thrown at the SEAM — the table's centre
  // line — rather than over your own rift. The only Shadow Gambling
  // card that does not throw over its owner.
  tome_daft_fantasy: tome("Daft Fantasy", "Shadow Gambling", 3, [
    coin(280, P.jynxi, { to: "seam", ms: 700, r: 1.8, size: 0.3, spin: 8 }),
    glyph(860, P.swarm, { to: "seam", ms: 480, tone: "?", size: 40, r: 0.6 }),
  ], 1360),

  // "Your adversary guesses what Ilk they each are." Two cards, two
  // guesses, thrown at the seam.
  tome_goodbye_horses: tome("Goodbye Horses", "Shadow Gambling", 3, [
    coin(280, P.jynxi, { to: "seam", ms: 700, r: 1.7, size: 0.28, spin: 8 }),
    coin(360, P.contract, { to: "seam", ms: 720, r: 2.0, size: 0.28, spin: 10 }),
    ...toll(P.jynxi, 1000, 2, "seam"),
  ], 1480),

  // "Your NEXT Shadow Gambling is an automatic win." It does not
  // resolve anything now — it loads the next one. So the coin lands and
  // a halo stays up: a standing state, not an outcome.
  tome_casino_royale: tome("Casino Royale", "Shadow Gambling", 3, [
    coin(280, P.swarm, { ...S, ms: 700, r: 1.9, size: 0.32, spin: 9 }),
    halo(880, P.jynxi, { ...S, ms: 720, r: 1.05, size: 0.85, spin: 1.5 }),
    star(920, P.emberGold, { ...S, ms: 400, r: 1.4, size: 0.05 }),
  ], 1620),

  // "Roll 2 dice. You draw for every incorrect match; your adversary
  // draws for every correct one." The Tome twin of the Lucky Dragon's
  // U'hlti, and it quotes it deliberately — two coins, then the mark.
  tome_dragon_deyes: tome("Dragon D'eyes", "Shadow Gambling", 3, [
    coin(280, P.dragon, { ...S, ms: 720, r: 1.9, size: 0.31, spin: 9 }),
    coin(360, P.jynxi, { ...S, ms: 760, r: 2.2, size: 0.31, spin: 11 }),
    glyph(1000, P.dragon, { ...S, ms: 500, tone: "⚅", size: 42, r: 0.7 }),
  ], 1560),

  // "Add any 2 cards from your Grimoire or Necropolis." A lucky cat, so
  // the payout is the loudest in the school: coin, then two rifts.
  tome_gambits_lucky_cat: tome("Gambit's Lucky Cat", "Shadow Gambling", 3, [
    coin(280, P.jynxi, { ...S, ms: 720, r: 2.0, size: 0.31, spin: 9 }),
    rift(940, P.jynxi, { ...S, ms: 440, r: 1.2, size: 0.032, size2: 0.24, tone: -9 }),
    rift(1030, P.jynxi, { ...S, ms: 440, r: 1.2, size: 0.032, size2: 0.24, tone: 9 }),
  ], 1560),

// ═══════════════════════════════════════════════════════════════════
// CONTRACT · "a legally binding offer to your enemy"
// ═══════════════════════════════════════════════════════════════════
// Nine Tomes that do something to BOTH Warlocks, and the CHAIN is the
// only primitive in the vocabulary with two ends — which is precisely
// why it was built. It sags, then SNAPS TAUT, and the snap is the
// moment the deal closes. Everything each card does individually hangs
// off that one instant.

  // "You and your adversary draw 1 card each." The cheapest contract in
  // the game at 1 Khao, and the plainest: chain, snap, two rifts.
  tome_devils_advocate: tome("The Devil's Advocate", "Contract", 1, [
    chain(280, P.contract, { from: "self", to: "target", ms: 560, r: 0.85, size: 0.095 }),
    rift(620, P.rust, { ...S, ms: 420, r: 1.0, size: 0.03, size2: 0.22 }),
    rift(620, P.rust, { ms: 420, r: 1.0, size: 0.03, size2: 0.22 }),
  ], 1140),

  // "You and your adversary gain 5 Anima each." Warmth on both halves,
  // fired simultaneously — the symmetry IS the card.
  tome_monkey_suite: tome("Monkey Suite", "Contract", 2, [
    chain(280, P.contract, { from: "self", to: "target", ms: 580, r: 0.85, size: 0.1 }),
    embers(660, P.animaGreen, { ...S, ms: 560, n: 24, r: 0.7, size: 0.07, size2: 1.6 }),
    embers(660, P.animaGreen, { ms: 560, n: 24, r: 0.7, size: 0.07, size2: 1.6 }),
  ], 1300),

  // "You and your adversary both add 1 Blood Contract to your hand."
  // Judge, Jury, Executioner — the scales, and they are the only glyph
  // in this school because this is the card that names the court.
  tome_judge_jury: tome("Judge, Jury, Executioner", "Contract", 3, [
    chain(280, P.contract, { from: "self", to: "target", ms: 620, r: 0.9, size: 0.11 }),
    glyph(660, P.judgeGold, { to: "seam", ms: 560, tone: "⚖", size: 44, r: 0.65 }),
    rift(780, P.contract, { ...S, ms: 440, r: 1.1, size: 0.032, size2: 0.24 }),
    rift(780, P.contract, { ms: 440, r: 1.1, size: 0.032, size2: 0.24 }),
    ...toll(P.contract, 820, 2, "seam"),
  ], 1500),

  // "Every turn your adversary doesn't Lyt Attack you, they gain 1
  // Anima." A restraint — so the chain snaps and then a rune CLOSES on
  // their half. They are bound, not paid.
  tome_restraining_ordeal: tome("Restraining Ordeal", "Contract", 1, [
    chain(280, P.contract, { from: "self", to: "target", ms: 560, r: 0.8, size: 0.095 }),
    rune(620, P.rust, { ms: 640, r: 1.4, n: 10, spin: 0.4, tone: "close" }),
  ], 1300),

  // "Your adversary must CHOOSE 1 of the following." A choice put in
  // their hands — so a coin lands on their side of the table. The only
  // Contract card that gambles, because the outcome genuinely is not
  // yours to decide.
  tome_agree_to_disagree: tome("Agree to Disagree", "Contract", 2, [
    chain(280, P.contract, { from: "self", to: "target", ms: 580, r: 0.85, size: 0.1 }),
    coin(640, P.contract, { ms: 680, r: 1.6, size: 0.29, spin: 8 }),
  ], 1400),

  // "BOTH Warlocks must ruin 1 of their own beings and its Soul." The
  // cruellest contract in the game — two severs, one per half, and the
  // second one is yours.
  tome_solomon_godfry: tome("Solomon Godfry's Manslaughter", "Contract", 3, [
    chain(280, P.contract, { from: "self", to: "target", ms: 620, r: 0.9, size: 0.11 }),
    sever(700, P.omnia, { ms: 580, r: 1.8, size: 0.045, size2: 0.3, tone: -18 }),
    sever(860, P.omnia, { ...S, ms: 580, r: 1.8, size: 0.045, size2: 0.3, tone: 18 }),
    ...toll(P.crimson, 900, 2, "seam"),
  ], 1560),

  // "Dim your own Anima by 4, then draw 1. Ruined once your adversary
  // devotes 4 Anima." A debt written in stone — the Magma Carta — so
  // heat comes off your own side while the chain holds.
  tome_magma_carta: tome("Magma Carta", "Contract", 1, [
    chain(280, P.contract, { from: "self", to: "target", ms: 580, r: 0.85, size: 0.1 }),
    embers(400, P.rust, { ...S, ms: 600, n: 24, r: 0.6, size: 0.075, size2: 1.5 }),
    rift(720, P.contract, { ...S, ms: 420, r: 1.0, size: 0.03, size2: 0.22 }),
  ], 1240),

  // "You and your adversary choose 1 of each other's Tomes to ruin."
  // Mutual destruction, and the only Contract that shatters — twice,
  // once per half, together.
  tome_seize_and_disease: tome("Seize and Disease", "Contract", 2, [
    chain(280, P.contract, { from: "self", to: "target", ms: 600, r: 0.88, size: 0.1 }),
    shatter(700, P.omnia, { ms: 600, n: 12, r: 1.4 }),
    shatter(700, P.omnia, { ...S, ms: 600, n: 12, r: 1.4 }),
    ...toll(P.contract, 740, 2, "seam"),
  ], 1420),

  // "Your adversary must DEVOTE to you at least 2 of their Anima."
  // The only card in the game that takes Anima and gives it to you — so
  // the stream runs the wrong way down the chain, from their plate to
  // yours, and you end up wearing a crown.
  tome_king_of_the_castle: tome("King of the Castle", "Contract", 3, [
    chain(280, P.contract, { from: "self", to: "target", ms: 620, r: 0.9, size: 0.11 }),
    stream(680, P.animaGreen, { from: "target", to: "self", ms: 660, n: 46, r: 0.22, size: 0.06 }),
    halo(1120, P.gold, { ...S, ms: 700, r: 1.15, size: 0.88, spin: 1.3 }),
    ...toll(P.contract, 1160, 2, "self"),
  ], 1880),
};

// ═══════════════════════════════════════════════════════════════════
// THE CURIOS · "you brought this with you, and that is the whole trick"
// ═══════════════════════════════════════════════════════════════════
// Nine cards that break the fourth wall: a Curio's condition is checked
// against the PLAYER — what you are wearing, what is sitting at the
// table next to you — and not against the board at all. Every one of
// them grants Anima and most auto-activate at duel start.
//
// So they share one shape and it is unlike anything else in the game:
// NO rune, NO ring on the felt, NO travel. The glyph of the object
// simply appears over the Curio rift and warmth comes off it. A Curio
// is not cast; it is ACKNOWLEDGED — the game noticing something that
// was already true about you before the duel began.
//
// The `n` on embers is the Anima granted. Wizard's Cap gives 3 and has
// the most; Circle of Lyf gives 1 and has the fewest. It is a small
// thing and nobody will consciously read it, but it is free and it is
// the difference between a system and a set of decorations.

// `own` is the one gesture that makes each Curio itself. The shared
// part stays shared — the mark appears, warmth comes off it, the felt
// acknowledges — because a Curio is a SCHOOL of moment and must read as
// one. What the ninth argument buys is that the hat and the ring are
// not the same hat.
const curio = (name, mark, hue, anima, own = []) => score(name, [
  glyph(0, hue, { ...S, ms: 620, tone: mark, size: 34, r: 0.55 }),
  embers(180, P.animaGreen, {
    ...S, ms: 520 + anima * 60, n: 10 + anima * 8,
    r: 0.4 + anima * 0.1, size: 0.07, size2: 1.1 + anima * 0.25,
  }),
  ...own,
  ...toll(hue, 260, anima >= 3 ? 2 : 1, "self"),
], 900 + anima * 80);

export const CURIOS = {
  // A wizard's hat is the only Curio worth 3 Anima and the only one
  // that is itself magical — so it gets a rune, the spellwork mark, and
  // it is the only Curio that does.
  curio_wizards_cap: curio("Wizard's Cap", "🜲", P.sorcery, 3, [
    rune(140, P.sorcery, { ...S, ms: 560, r: 0.9, n: 8, spin: 0.7 }),
  ]),
  // A RING. A circle, closing — the one Curio whose shape is stated
  // outright in its name, so it would be perverse to draw anything else.
  curio_circle_of_lyf: curio("Circle of Lyf", "◎", P.goldPale, 1, [
    rune(120, P.goldPale, { ...S, ms: 480, r: 0.5, n: 0, spin: 1.2, tone: "close" }),
  ]),
  // Official J'ynxi Wares. She is watching, she is delighted, and every
  // single thing of hers in this game gambles.
  curio_jynxi_wares: curio("J'ynxi's Wares", "⚄", P.jynxi, 2, [
    coin(130, P.jynxi, { ...S, ms: 560, r: 1.0, size: 0.22, spin: 6 }),
  ]),
  // Glasses. You can SEE now — the scan is the looking, borrowed from
  // Ancient Sorcery and used here for a joke rather than a search.
  curio_spectacles: curio("Spectacular Spectacles", "◉", P.cyanHot, 1, [
    scan(120, P.cyanHot, { ...S, ms: 520, r: 0.85, size: 0.04 }),
  ]),
  // An amulet hangs and turns. The smallest halo in the game.
  curio_mages_amulet: curio("Mage's Amulet", "❖", P.amethyst, 1, [
    halo(140, P.amethyst, { ...S, ms: 560, r: 0.55, size: 0.5, spin: 1.8 }),
  ]),
  // You brought a potion and you drank it. Vapour comes off it — the
  // only warm plume in the library, and the reason the primitive takes
  // a `tone` at all.
  curio_elixir: curio("Elixir of Health", "⚗", P.returnGreen, 1, [
    plume(130, P.returnGreen, { ...S, ms: 520, n: 16, r: 0.4, size: 0.08, size2: 0.7, tone: 0.55 }),
  ]),
  // An outer garment. It CLOSES around you, which is a veil — the same
  // shape every ward in the game wears, at a quarter of the size.
  curio_warlocks_robe: curio("Warlock's Robe", "⛨", P.violetDeep, 2, [
    veil(130, P.violetDeep, { ...S, ms: 560, r: 0.95, size: 0.6 }),
  ]),
  // ── THE TWO COMPANION CURIOS ────────────────────────────────────
  // "If you have 1 inanimate companion at the duel table with you."
  // These check for something SITTING BESIDE YOU — the furthest outside
  // the game any card in Terror reaches — so they are the two that get
  // the ember-warm hearth colour, and the two that face outward.
  //
  // Beb Buddy raises Max Khao rather than granting Anima, the only
  // Curio that touches the other currency, so its accent is cool where
  // its glyph is warm.
  curio_beb_buddy: curio("Beb Buddy", "❤", P.rust, 1, [
    embers(140, P.khaoBlue, { ...S, ms: 520, n: 12, r: 0.35, size: 0.06, size2: 0.9 }),
  ]),
  // "If your inanimate companion is wearing any sort of extra wear."
  // Your companion is dressed up. It gets a star. That is the whole card
  // and it is a delight.
  curio_badass_beb: curio("Badass Beb", "★", P.emberGold, 2, [
    star(150, P.emberGold, { ...S, ms: 400, r: 1.1, size: 0.045 }),
  ]),
};

// ═══════════════════════════════════════════════════════════════════
// THE PROPHECIES · "an ambition spoken aloud"
// ═══════════════════════════════════════════════════════════════════
// Eight alternate victory conditions, declared face-down before the
// duel and immune to every card in the game (R74). H21 gave them the
// stillest arrival in the language — "the card holds perfectly still
// while the light comes to it" — and this is what happens when one is
// actually DECLARED.
//
// The shape is fixed and it is the most solemn thing on the board: a
// column, a ring that inscribes itself very slowly, and a crown that
// does not leave. Nothing travels, nothing breaks, nothing is aimed at
// anyone. A Prophecy is not an attack; it is a statement of intent that
// the game has agreed to honour.
//
// The four Ilk Prophecies take their own Ilk's hue. The two Tablets and
// the two odd ones take indigo — the type colour — because they belong
// to no Ilk.

const prophecy = (name, hue, mark) => score(name, [
  pillar(0, hue, { ...S, ms: 820, r: 6.4, size: 0.42, spin: 0.15 }),
  rune(180, hue, { ...S, ms: 900, r: 2.2, n: 20, spin: 0.2 }),
  // ── THE ALIGNMENT ──────────────────────────────────────────────
  // Every Prophecy in the game is a set of conditions COMING INTO
  // LINE — "if you have 2 Supreme Astro Beings and 1 A'aeternis Space
  // Being, you win the duel." That is not a spell and it is not an
  // attack; it is separate things turning out to be connected, which
  // is precisely and only what the constellation says.
  //
  // Its links draw one at a time, which is the reading order of the
  // condition itself: this, and this, and then this — and now it is
  // true. Nothing else in the vocabulary can make that sentence.
  constellation(360, hue, { ...S, ms: 820, r: 1.9, n: 6, size: 0.11 }),
  glyph(560, hue, { ...S, ms: 700, tone: mark, size: 50, r: 0.8 }),
  halo(760, hue, { ...S, ms: 840, r: 1.3, size: 0.95, spin: 0.8 }),
  ...toll(hue, 860, 3, "self"),
], 1800);

export const PROPHECIES = {
  // ── THE FOUR ILK PROPHECIES ─────────────────────────────────────
  // "If you have 2 Supreme [Ilk] Beings and 1 A'oeternis or A'aeternis
  // [Ilk] Being, you win the duel." One sentence, four times, with the
  // Ilk swapped — so the SHAPE is deliberately identical and the whole
  // point is that they are one idea in four colours.
  //
  // But identical shape plus identical mark was one step too far: all
  // four wore "✧" and the only difference was hue. Each now carries its
  // own Ilk's mark, which costs nothing and means a player reading the
  // board sees WHICH Prophecy is standing, not merely that one is.
  proph_space_race:      prophecy("Space Race", P.space, "✦"),
  // ☾ rather than ☠ — Bloody Hell already owns the skull (it wins on
  // 45 Anima lost, which is the one Prophecy that is genuinely about
  // corpses), and a Phantom PLANET should be a body in the sky.
  proph_phantom_planet:  prophecy("Phantom Planet", P.phantom, "☾"),
  proph_dragonforce:     prophecy("Dragonforce", P.dragon, "◈"),
  proph_time_to_pretend: prophecy("Time to Pretend", P.time, "❋"),
  // The Tablets — one wins on YOUR Supreme Beings, one wins on your
  // ADVERSARY's. O'mnis ruins, N'ullus nullifies; the marks are the
  // counter-families' own and they are the only Prophecies that are not
  // a star.
  proph_tablet_omnis:    prophecy("Tablet of O'mnis", P.indigo, "✖"),
  proph_tablet_nullus:   prophecy("Tablet of N'ullus", P.indigo, "⊘"),
  // "If you win 7 Shadow Gamblings in a row, you win the duel." The
  // only Prophecy that is a gamble, so it wears J'ynxi's green and her
  // die instead of the indigo.
  proph_ace_of_shades:   prophecy("Ace of Shades", P.jynxi, "⚄"),
  // "If a Warlock has lost 45 or more Anima, you win the duel." The
  // only Prophecy that wins on carnage rather than on assembly.
  proph_bloody_hell:     prophecy("Bloody Hell", P.omnia, "☠"),
};

// ═══════════════════════════════════════════════════════════════════
// THE AFFINITIES · "the alignment closes; nothing else gets in"
// ═══════════════════════════════════════════════════════════════════
// Four exist, ever. One per Grimoire, maximum. Khao cost zero — they
// cost nothing but EVERYTHING BEFORE THEM, since an Affinity may only
// crown a Lord that already carries a U'hlti.
//
// `seal()` carries the whole school: a ring running INWARD, a crown that
// stays, and the alignment's mark. What each of the four adds is the
// one thing only it does — and all four are denial effects, which is
// the shape of the top of this ladder. Not more damage. More refusal.

export const AFFINITIES = {
  // "Nullify any 1 attack." Y'tyranus, the Holy alignment. The purest
  // refusal in the game, so it takes Nihil's own verb and wears it in
  // gold: the veil, inside the seal.
  aff_holy: score("Y'tyranus", [
    ...seal("Holy"),
    veil(420, P.holy, { ...S, ms: 700, r: 1.7, size: 0.82 }),
    star(700, P.judgeGold, { ...S, ms: 460, r: 2.0, size: 0.06 }),
    ...toll(P.holy, 740, 3, "self"),
  ], 1560, { surge: SURGE.soft(P.holy, 420) }),

  // "Nullify 1 AFFINITY." The only card in the game that answers the
  // top of the ladder — Enlightenment "answers the answer" — so it is
  // the only score that closes a second ring INSIDE the first. A seal
  // sealing a seal, and nothing else in Terror makes that shape.
  aff_awoken: score("Enlightenment", [
    ...seal("Awoken"),
    rune(460, P.awoken, { ...S, ms: 700, r: 1.1, n: 10, spin: 0.9, tone: "close" }),
    // "It answers the answer" — the only card in the game that
    // nullifies an Affinity, and the only Affinity that sends
    // something back rather than merely refusing it.
    mirror(620, P.awoken, { from: "target", to: "self", ms: 640, r: 1.05 }),
    // seal() already hangs a halo here — a SECOND one was two crowns
    // on one card and seven live cues at 840ms. The mirror is the new
    // idea and the one worth the room; the extra crown was decoration.
    ...toll(P.awoken, 760, 3, "self"),
  ], 1580, { surge: SURGE.soft(P.awoken, 460) }),

  // "Send 1 card to Necropolis, except another Affinity." X'aol —
  // the Evil alignment, and the only Affinity that destroys. It gets
  // the execution verb, at the only scale a card with no Khao cost and
  // an entire ladder behind it deserves.
  aff_evil: score("X'aol", [
    ...seal("Evil"),
    sever(460, P.omnia, { ms: 680, r: 2.3, size: 0.055, size2: 0.4, tone: -20 }),
    shatter(700, P.evil, { ms: 700, n: 16, r: 1.8 }),
    ...toll(P.evil, 740, 3),
  ], 1620, { surge: SURGE.full(P.evil, 460) }),

  // "Add 1 Shadow Gambling from your Grimoire or Necropolis; cannot
  // play the same one twice in a row." Friend or Faux — J'ynxi's own,
  // the secret third faction, and she "is watching, and she is
  // delighted." The only Affinity that gambles, and the only one whose
  // crown spins the wrong way.
  aff_jynxi: score("Friend or Faux", [
    ...seal("J'ynxi"),
    coin(440, P.jest, { ...S, ms: 720, r: 2.0, size: 0.32, spin: 10 }),
    rift(1060, P.jest, { ...S, ms: 460, r: 1.3, size: 0.034, size2: 0.26 }),
    ...toll(P.jest, 1100, 3, "self"),
  ], 1820, { surge: SURGE.soft(P.jest, 440) }),
};

// ── THE CODEX VIEW ─────────────────────────────────────────────────
export const describeSpellbook = () => {
  const rows = [];
  const push = (channel, reg) => Object.keys(reg).forEach((k) =>
    rows.push({ channel, key: k, name: reg[k].name, ms: reg[k].ms, cues: reg[k].cues.length }));
  push("TOME", TOMES);
  push("CURIO", CURIOS);
  push("PROPHECY", PROPHECIES);
  push("AFFINITY", AFFINITIES);
  return rows;
};

const tomeMotion = { TOMES, CURIOS, PROPHECIES, AFFINITIES, describeSpellbook };
export default tomeMotion;
